import { createFileRoute, notFound, Outlet, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { ApiError, api, qk, unwrap } from "@/lib/api";

export interface ProjectSearch {
  /** Selected environment name. Omitted means production. */
  env?: string;
}

export const Route = createFileRoute("/$workspace/$project")({
  validateSearch: (s: Record<string, unknown>): ProjectSearch => (typeof s.env === "string" && s.env ? { env: s.env } : {}),
  loader: async ({ params, context }) => {
    // Prefetch so the sidebar badges and environment switcher render instantly.
    try {
      await Promise.all([
        context.queryClient.ensureQueryData({
          queryKey: qk.projectStatus(params.project),
          queryFn: () => unwrap(api.GET("/v1/projects/{project}/status", { params: { path: { project: params.project } } })),
        }),
        context.queryClient.ensureQueryData({
          queryKey: qk.environments(params.project),
          queryFn: () => unwrap(api.GET("/v1/projects/{project}/environments", { params: { path: { project: params.project } } })),
        }),
      ]);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) throw notFound();
      throw e;
    }
  },
  component: ProjectLayout,
});

/** Screens people jump to with g-shortcuts or the palette, where there is no hover to preload on. */
const WARM = ["/$workspace/$project/", "/$workspace/$project/logs", "/$workspace/$project/metrics", "/$workspace/$project/safety", "/$workspace/$project/deployments/", "/$workspace/$project/spend"] as const;

function ProjectLayout() {
  const router = useRouter();
  // Once the project is on screen and the browser is idle, fetch the other screens' code so the first visit is instant.
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (h: number) => void };
    const warm = () => {
      for (const id of WARM) {
        const route = router.routesById[id];
        if (route) void router.loadRouteChunk(route)?.catch(() => undefined);
      }
    };
    if (w.requestIdleCallback) {
      const h = w.requestIdleCallback(warm, { timeout: 4000 });
      return () => w.cancelIdleCallback?.(h);
    }
    const t = setTimeout(warm, 1500);
    return () => clearTimeout(t);
  }, [router]);
  return <Outlet />;
}
